// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import NewButton from '~/app/ui/Button';
import { S } from '~/app/ui/tokens';

type Props = {
  color?: String,
  children: React.ReactNode,
  primary?: boolean,
  secondary?: boolean,
  inverted?: boolean,
  disabled?: boolean,
  size?: 'sm' | 'md' | 'lg',
  onClick: () => void,
  className?: String,
  style?: Object,
}

const useStyles = createUseStyles({
  lg: { height: 40, padding: '0 18px', fontSize: 15 },
  // A button filled with its own color: same shape, a gentle hover.
  custom: { '&:hover': { filter: 'brightness(1.1)' } }
});

/**
 * The older screens' button, now drawn by the admin's Button (app/ui/Button) so
 * every button looks and behaves the same: `primary` (or no style) is the filled
 * main action, `secondary`/`inverted` the outlined one. A `color` fills it with
 * that color instead. New code should use app/ui/Button directly.
 */
function Button({
  color, primary, secondary, inverted, size, className, style, children, ...rest
}: Props) {
  const c = useStyles();
  const outlined = Boolean(inverted || secondary);
  const custom = !primary && !outlined && color && color !== S.fg ? color : null;
  return (
    <NewButton
      {...rest}
      variant={outlined ? 'secondary' : 'primary'}
      size={size === 'sm' ? 'sm' : 'md'}
      className={classNames(size === 'lg' && c.lg, custom && c.custom, className)}
      style={custom ? { background: custom, borderColor: custom, ...style } : style}
    >
      {children}
    </NewButton>
  );
}

Button.defaultProps = {
  color: S.fg
};

export default Button;
